import { useState, useCallback, useRef, useEffect } from 'react';
import {
  ProductoCatalogoPublico,
  FiltrosDisponibles,
  ListadoParams,
} from '../types';
import { CatalogoService } from '../services/catalogoService';

const DEBOUNCE_MS = 300;
const LIMITE = 20;

export interface FiltrosActivos {
  texto: string;
  categoriaIds: number[];
  subcategoriaIds: number[];
  tags: string[];
  precioMin: string;
  precioMax: string;
  orden: 'precio_asc' | 'precio_desc' | 'nombre_asc';
  color: string | null;
  conStock: boolean;
}

const FILTROS_INICIALES: FiltrosActivos = {
  texto: '',
  categoriaIds: [],
  subcategoriaIds: [],
  tags: [],
  precioMin: '',
  precioMax: '',
  orden: 'nombre_asc',
  color: null,
  conStock: false,
};

interface UseCatalogoState {
  productos: ProductoCatalogoPublico[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  filtros: FiltrosActivos;
  filtrosDisponibles: FiltrosDisponibles | null;
  loadingFiltros: boolean;
  proveedorIds: number[];
  subcategoriasSeleccionadas: number[];
}

interface UseCatalogoActions {
  cambiarFiltro: <K extends keyof FiltrosActivos>(key: K, value: FiltrosActivos[K]) => void;
  limpiarFiltros: () => void;
  cargarMas: () => void;
  setProveedores: (ids: number[]) => void;
  toggleSubcategoria: (id: number) => void;
  toggleCategoriaCompleta: (ids: number[]) => void;
  limpiarSubcategorias: () => void;
}

function buildParams(f: FiltrosActivos, pIds: number[], catProvIds: number[], cursor?: string | null): ListadoParams {
  const params: ListadoParams = { limite: LIMITE };
  if (cursor) params.cursor = cursor;
  if (f.texto.trim()) params.q = f.texto.trim();
  if (f.categoriaIds.length > 0) params.categoriaIds = f.categoriaIds;
  if (f.subcategoriaIds.length > 0) params.subcategoriaIds = f.subcategoriaIds;
  if (f.tags.length > 0) params.tags = f.tags;
  if (f.precioMin.trim()) { const n = parseFloat(f.precioMin); if (!isNaN(n)) params.precioMin = n; }
  if (f.precioMax.trim()) { const n = parseFloat(f.precioMax); if (!isNaN(n)) params.precioMax = n; }
  if (f.conStock) params.conStock = true;
  if (f.color) params.color = f.color;
  params.orden = f.orden;
  if (pIds.length > 0) params.proveedorIds = pIds;
  if (catProvIds.length > 0 && pIds.length > 0) params.categoriaProveedorIds = catProvIds;
  return params;
}

export function useCatalogo(): UseCatalogoState & UseCatalogoActions {
  const [productos, setProductos] = useState<ProductoCatalogoPublico[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const nextCursorRef = useRef<string | null>(null);

  const [filtros, setFiltros] = useState<FiltrosActivos>(FILTROS_INICIALES);
  const [filtrosDisponibles, setFiltrosDisponibles] = useState<FiltrosDisponibles | null>(null);
  const [loadingFiltros, setLoadingFiltros] = useState(true);
  const [proveedorIds, setProveedorIds] = useState<number[]>([]);
  const [subcategoriasSeleccionadas, setSubcategoriasSeleccionadas] = useState<number[]>([]);

  // Refs for fresh values in callbacks
  const filtrosRef = useRef(FILTROS_INICIALES);
  const proveedorIdsRef = useRef<number[]>([]);
  const subcatRef = useRef<number[]>([]);

  useEffect(() => { filtrosRef.current = filtros; }, [filtros]);
  useEffect(() => { proveedorIdsRef.current = proveedorIds; }, [proveedorIds]);
  useEffect(() => { subcatRef.current = subcategoriasSeleccionadas; }, [subcategoriasSeleccionadas]);

  const debounceTextoRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncePrecioRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchVersion = useRef(0);

  const ejecutarBusqueda = useCallback(
    async (f: FiltrosActivos, pIds: number[], subcats: number[]) => {
      const version = ++searchVersion.current;
      setLoading(true);
      setError(null);
      nextCursorRef.current = null;

      try {
        const result = await CatalogoService.getListado(buildParams(f, pIds, subcats));
        if (searchVersion.current !== version) return;
        setProductos(result.data);
        setHasMore(result.meta.hasMore);
        nextCursorRef.current = result.meta.nextCursor;
      } catch (err: unknown) {
        if (searchVersion.current !== version) return;
        setError(err instanceof Error ? err.message : 'Error al cargar productos');
      } finally {
        if (searchVersion.current === version) setLoading(false);
      }
    },
    []
  );

  // Load initial filtros on mount
  useEffect(() => {
    CatalogoService.getListadoFiltros()
      .then((f) => { setFiltrosDisponibles(f); setLoadingFiltros(false); })
      .catch(() => setLoadingFiltros(false));
    ejecutarBusqueda(FILTROS_INICIALES, [], []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch filtros panel when proveedorIds changes (serialized to string to avoid object identity issues)
  const proveedorIdsKey = proveedorIds.join(',');
  const prevProveedorIdsKey = useRef('');
  useEffect(() => {
    const key = proveedorIdsKey;
    if (key === prevProveedorIdsKey.current) return;
    prevProveedorIdsKey.current = key;
    CatalogoService.getListadoFiltros(proveedorIds)
      .then((f) => setFiltrosDisponibles(f))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveedorIdsKey]);

  const cambiarFiltro = useCallback(
    <K extends keyof FiltrosActivos>(key: K, value: FiltrosActivos[K]) => {
      const nuevos: FiltrosActivos = { ...filtrosRef.current, [key]: value };
      filtrosRef.current = nuevos;
      setFiltros(nuevos);

      if (key === 'texto') {
        if (debounceTextoRef.current) clearTimeout(debounceTextoRef.current);
        debounceTextoRef.current = setTimeout(() => {
          ejecutarBusqueda(filtrosRef.current, proveedorIdsRef.current, subcatRef.current);
        }, DEBOUNCE_MS);
        return;
      }

      if (key === 'precioMin' || key === 'precioMax') {
        if (debouncePrecioRef.current) clearTimeout(debouncePrecioRef.current);
        debouncePrecioRef.current = setTimeout(() => {
          ejecutarBusqueda(filtrosRef.current, proveedorIdsRef.current, subcatRef.current);
        }, DEBOUNCE_MS);
        return;
      }

      ejecutarBusqueda(nuevos, proveedorIdsRef.current, subcatRef.current);
    },
    [ejecutarBusqueda]
  );

  const setProveedores = useCallback(
    (ids: number[]) => {
      proveedorIdsRef.current = ids;
      setProveedorIds(ids);

      let subcats: number[] = subcatRef.current;
      if (ids.length === 0 && subcats.length > 0) {
        subcats = [];
        subcatRef.current = [];
        setSubcategoriasSeleccionadas([]);
      }

      ejecutarBusqueda(filtrosRef.current, ids, subcats);
    },
    [ejecutarBusqueda]
  );

  const toggleSubcategoria = useCallback(
    (id: number) => {
      const prev = subcatRef.current;
      const next = prev.includes(id)
        ? prev.filter((s) => s !== id)
        : [...prev, id];
      subcatRef.current = next;
      setSubcategoriasSeleccionadas(next);
      ejecutarBusqueda(filtrosRef.current, proveedorIdsRef.current, next);
    },
    [ejecutarBusqueda]
  );

  const toggleCategoriaCompleta = useCallback(
    (ids: number[]) => {
      const prev = subcatRef.current;
      const todasSeleccionadas = ids.every((id) => prev.includes(id));
      const next = todasSeleccionadas
        ? prev.filter((id) => !ids.includes(id))
        : [...prev.filter((id) => !ids.includes(id)), ...ids];
      subcatRef.current = next;
      setSubcategoriasSeleccionadas(next);
      ejecutarBusqueda(filtrosRef.current, proveedorIdsRef.current, next);
    },
    [ejecutarBusqueda]
  );

  const limpiarSubcategorias = useCallback(() => {
    subcatRef.current = [];
    setSubcategoriasSeleccionadas([]);
    ejecutarBusqueda(filtrosRef.current, proveedorIdsRef.current, []);
  }, [ejecutarBusqueda]);

  const limpiarFiltros = useCallback(() => {
    if (debounceTextoRef.current) clearTimeout(debounceTextoRef.current);
    if (debouncePrecioRef.current) clearTimeout(debouncePrecioRef.current);
    filtrosRef.current = FILTROS_INICIALES;
    proveedorIdsRef.current = [];
    subcatRef.current = [];
    setFiltros(FILTROS_INICIALES);
    setProveedorIds([]);
    setSubcategoriasSeleccionadas([]);
    ejecutarBusqueda(FILTROS_INICIALES, [], []);
  }, [ejecutarBusqueda]);

  const cargarMas = useCallback(() => {
    const cursor = nextCursorRef.current;
    if (!hasMore || loadingMore || !cursor) return;

    const version = ++searchVersion.current;
    setLoadingMore(true);

    CatalogoService.getListado(buildParams(filtrosRef.current, proveedorIdsRef.current, subcatRef.current, cursor))
      .then((result) => {
        if (searchVersion.current !== version) return;
        setProductos((prev) => [...prev, ...result.data]);
        setHasMore(result.meta.hasMore);
        nextCursorRef.current = result.meta.nextCursor;
      })
      .catch((err: unknown) => {
        if (searchVersion.current !== version) return;
        setError(err instanceof Error ? err.message : 'Error al cargar más');
      })
      .finally(() => {
        if (searchVersion.current === version) setLoadingMore(false);
      });
  }, [hasMore, loadingMore]);

  return {
    productos,
    loading,
    loadingMore,
    error,
    hasMore,
    filtros,
    filtrosDisponibles,
    loadingFiltros,
    proveedorIds,
    subcategoriasSeleccionadas,
    cambiarFiltro,
    limpiarFiltros,
    cargarMas,
    setProveedores,
    toggleSubcategoria,
    toggleCategoriaCompleta,
    limpiarSubcategorias,
  };
}
